import { StrategyMetadata, Basket } from '../types';
import { UL_RISK_TYPES, UL_TARGET_TYPES, UL_TSL_TYPES, UL_WAT_TYPES } from '../constants/tradingConstants';

/**
 * Phase 6 — derive `hasUl` from raw legs so the Baskets-page UL chip lights
 * up without a new backend field. Detected via the same UL type sets the
 * frontend validators use, so a leg-config edit that adds/removes a UL
 * type stays in sync across chip + validator + LegMatrix labels.
 *
 * Keys on `type` only (not `enabled`) to mirror backend `is_ul_*_config`
 * predicates in `libs/shared/shared/constants.py`. A disabled config with
 * a UL type is still a UL leg for cross-config purposes (Phase 6 review-fix
 * M4) — the frontend chip must reflect what the backend save-time validator
 * sees, else a user editing a legacy UL config with all features toggled
 * off would see no UL chip but hit a save-time UL cross-config rejection.
 *
 * Reads defensively: legs may be missing on a partial payload, or carry
 * pre-UL shapes where the risk-feature keys are absent.
 */
function detectHasUl(legs: any[] | undefined): boolean {
  if (!Array.isArray(legs)) return false;
  return legs.some((leg) => {
    if (!leg) return false;
    const slType  = leg.stop_loss?.type;
    const tpType  = leg.target_profit?.type;
    const tslType = leg.trailing_stop_loss?.type;
    const watType = leg.wait_and_trade?.type;
    return (
      (typeof slType  === 'string' && UL_RISK_TYPES.includes(slType))   ||
      (typeof tpType  === 'string' && UL_TARGET_TYPES.includes(tpType)) ||
      (typeof tslType === 'string' && UL_TSL_TYPES.includes(tslType))   ||
      (typeof watType === 'string' && UL_WAT_TYPES.includes(watType))
    );
  });
}

/**
 * Transform strategy fields from backend snake_case to frontend camelCase
 * Updated to use clean StrategyMetadata interface with legCount
 */
export function transformStrategyFields(backendStrategy: any): StrategyMetadata {
  return {
    strategyId: backendStrategy.strategy_id,
    strategyName: backendStrategy.strategy_name,
    strategyType: backendStrategy.strategy_type || backendStrategy.underlying || 'Unknown',
    status: backendStrategy.status || 'ACTIVE',
    legCount: backendStrategy.leg_count || backendStrategy.legs?.length || 0,
    // Derive max leg.lots from raw legs payload so the allocator UI can cap
    // lot_multiplier without an N+1 fetch. Backend already returns legs on
    // the basket-strategies endpoint; we just collapse them to a single number.
    maxLegLots: Array.isArray(backendStrategy.legs) && backendStrategy.legs.length > 0
      ? backendStrategy.legs.reduce(
          (max: number, leg: any) => Math.max(max, Number(leg?.lots) || 0),
          0,
        )
      : 0,
    createdAt: backendStrategy.created_at,
    displayOrder: backendStrategy.display_order,

    // Phase 2: Add missing field mappings for strategy editing
    moveSlToCost: backendStrategy.move_sl_to_cost || false,
    rangeBreakout: backendStrategy.range_breakout || false,
    rangeBreakoutTime: backendStrategy.range_breakout_time || undefined,
    orbContinueOvernight: backendStrategy.orb_continue_overnight || false,
    // Phase 6 — derived (not backend-projected) UL feature flag.
    hasUl: detectHasUl(backendStrategy.legs),
    tradingType: backendStrategy.trading_type || 'INTRADAY',
    intradayExitMode: backendStrategy.intraday_exit_mode || 'SAME_DAY',

    // Configuration fields that might also be needed
    underlying: backendStrategy.underlying,
    expiryType: backendStrategy.expiry_type,
    product: backendStrategy.product,
    description: backendStrategy.description,
    entryTime: backendStrategy.entry_time,
    exitTime: backendStrategy.exit_time,
    entryDays: backendStrategy.entry_days,
    exitDays: backendStrategy.exit_days,
    // INTRADAY entry-selection (back-compat: defaults to undefined when
    // backend row hasn't migrated, treated as WEEKDAY by consumers).
    entrySelectionMode: backendStrategy.entry_selection_mode === 'DTE' ? 'DTE'
      : backendStrategy.entry_selection_mode === 'WEEKDAY' ? 'WEEKDAY'
      : undefined,
    entryDtes: Array.isArray(backendStrategy.entry_dtes)
      ? backendStrategy.entry_dtes.map((d: number | string) => Number(d))
      : undefined,

    // POSITIONAL trading fields for derived trading type calculation
    entryTradingDaysBeforeExpiry: backendStrategy.entry_trading_days_before_expiry,
    exitTradingDaysBeforeExpiry: backendStrategy.exit_trading_days_before_expiry,

    // Strategy-level risk management
    targetProfit: backendStrategy.target_profit ? {
      enabled: true,
      type: backendStrategy.target_profit.type || 'PERCENTAGE',
      value: backendStrategy.target_profit.value || 0
    } : undefined,
    stopLoss: backendStrategy.mtm_stop_loss ? {
      enabled: true,
      type: (backendStrategy.mtm_stop_loss)?.type || 'PERCENTAGE',
      value: (backendStrategy.mtm_stop_loss)?.value || 0
    } : undefined,

    // Note: isIntraDay and sequenceNumber are now handled in StrategyWithLegs for editing mode
  };
}

/**
 * Transform basket with strategy field transformation
 */
export function transformBasket(backendBasket: any): Basket {
  return {
    ...backendBasket,
    strategies: backendBasket.strategies?.map(transformStrategyFields) || [],
  };
}